import { Card, Table, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaSortUp, FaSortDown, FaSort, FaPen } from 'react-icons/fa';
import Pagination from '../common/Pagination';
import { useAuth } from '../../context/AuthContext';

const ReportTable = ({ reportType, data, sortConfig, onSort, pagination, onPageChange }) => {
    const { user } = useAuth();
    const isEmployee = user?.role === 'employee';
    
    // Define columns for each report type
    const getColumns = () => {
        let columns = [];
        
        switch (reportType) {
            case 'attendance':
                columns = [
                    { key: 'employeeName', label: 'Employee Name', sortable: true },
                    { key: 'employeeId', label: 'Emp Code', sortable: true },
                    { key: 'department', label: 'Department', sortable: true },
                    { key: 'totalWorkingDays', label: 'Working Days', sortable: true },
                    { key: 'presentDays', label: 'Present', sortable: true },
                    { key: 'absentDays', label: 'Absent', sortable: true },
                    { key: 'lateMarks', label: 'Late Marks', sortable: true },
                    { key: 'attendancePercentage', label: 'Attendance %', sortable: true, format: 'percentage' }
                ];
                break;
            case 'leave':
                columns = [
                    { key: 'employeeName', label: 'Employee Name', sortable: true },
                    { key: 'employeeId', label: 'Emp Code', sortable: true },
                    { key: 'department', label: 'Department', sortable: true },
                    { key: 'totalLeaveBalance', label: 'Total Balance', sortable: true },
                    { key: 'leavesTaken', label: 'Taken', sortable: true },
                    { key: 'pendingRequests', label: 'Pending', sortable: true },
                    { key: 'approvedLeaves', label: 'Approved', sortable: true },
                    { key: 'rejectedLeaves', label: 'Rejected', sortable: true },
                    { key: 'remainingBalance', label: 'Remaining', sortable: true }
                ];
                break;
            case 'payroll':
                columns = [
                    { key: 'employeeName', label: 'Employee Name', sortable: true },
                    { key: 'employeeId', label: 'Emp Code', sortable: true },
                    { key: 'department', label: 'Department', sortable: true },
                    { key: 'basicSalary', label: 'Basic Salary', sortable: true, format: 'currency' },
                    { key: 'allowances', label: 'Allowances', sortable: true, format: 'currency' },
                    { key: 'grossSalary', label: 'Gross Salary', sortable: true, format: 'currency' },
                    { key: 'deductions', label: 'Deductions', sortable: true, format: 'currency' },
                    { key: 'netSalary', label: 'Net Salary', sortable: true, format: 'currency' },
                    { key: 'paymentStatus', label: 'Status', sortable: true, format: 'badge' }
                ];
                break;
            case 'overtime':
                columns = [
                    { key: 'employeeName', label: 'Employee Name', sortable: true },
                    { key: 'employeeId', label: 'Emp Code', sortable: true },
                    { key: 'department', label: 'Department', sortable: true },
                    { key: 'totalOvertimeHours', label: 'OT Hours', sortable: true },
                    { key: 'overtimeDays', label: 'OT Days', sortable: true },
                    { key: 'hourlyRate', label: 'Hourly Rate', sortable: true, format: 'currency' },
                    { key: 'overtimePay', label: 'OT Pay', sortable: true, format: 'currency' }
                ];
                break;
            case 'late-marks':
                columns = [
                    { key: 'employeeName', label: 'Employee Name', sortable: true },
                    { key: 'employeeId', label: 'Emp Code', sortable: true },
                    { key: 'department', label: 'Department', sortable: true },
                    { key: 'lateMarks', label: 'Late Marks', sortable: true },
                    { key: 'dateRange', label: 'Date Range', sortable: false }
                ];
                break;
            case 'employee-movement':
                columns = [
                    { key: 'employeeName', label: 'Employee Name', sortable: true },
                    { key: 'employeeId', label: 'Emp Code', sortable: true },
                    { key: 'department', label: 'Department', sortable: true },
                    { key: 'designation', label: 'Designation', sortable: true },
                    { key: 'movementType', label: 'Type', sortable: true, format: 'badge' },
                    { key: 'date', label: 'Date', sortable: true },
                    { key: 'status', label: 'Status', sortable: true, format: 'badge' }
                ];
                break;
            case 'loan-advance':
                columns = [
                    { key: 'employeeName', label: 'Employee Name', sortable: true },
                    { key: 'employeeId', label: 'Emp Code', sortable: true },
                    { key: 'department', label: 'Department', sortable: true },
                    { key: 'loanType', label: 'Type', sortable: true },
                    { key: 'amount', label: 'Amount', sortable: true, format: 'currency' },
                    { key: 'approvedDate', label: 'Approved Date', sortable: true },
                    { key: 'repaymentStatus', label: 'Status', sortable: true, format: 'badge' },
                    { key: 'remainingAmount', label: 'Remaining', sortable: true, format: 'currency' }
                ];
                break;
            case 'department-wise':
                columns = [
                    { key: 'departmentName', label: 'Department', sortable: true },
                    { key: 'totalEmployees', label: 'Total Employees', sortable: true },
                    { key: 'totalWorkingDays', label: 'Working Days', sortable: true },
                    { key: 'presentDays', label: 'Present', sortable: true },
                    { key: 'absentDays', label: 'Absent', sortable: true },
                    { key: 'lateMarks', label: 'Late Marks', sortable: true },
                    { key: 'attendancePercentage', label: 'Attendance %', sortable: true, format: 'percentage' }
                ];
                break;
            default:
                columns = [];
        }
        
        return columns;
    };

    const columns = getColumns();

    const formatValue = (value, format, row) => {
        if (value === null || value === undefined || value === 'N/A') return 'N/A';

        // Special handling for Employee Name in Attendance report to show update indicator
        if (reportType === 'attendance' && row && row.hasUpdates === 'Yes' && format === undefined && String(value) === row.employeeName) {
            return (
                <div className="d-flex align-items-center">
                    {value}
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Attendance record updated manually</Tooltip>}
                    >
                        <span className="ms-2 text-warning">
                            <FaPen size={12} />
                        </span>
                    </OverlayTrigger>
                </div>
            );
        }

        switch (format) {
            case 'currency':
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(value);
            case 'percentage':
                return `${value}%`;
            case 'badge':
                const badgeColor = getBadgeColor(value);
                return (
                    <span className={`badge bg-${badgeColor}`}>
                        {value}
                    </span>
                );
            default:
                return value;
        }
    };

    const getBadgeColor = (value) => {
        const lowerValue = String(value).toLowerCase();
        if (lowerValue === 'paid' || lowerValue === 'approved' || lowerValue === 'active' || lowerValue === 'joining') {
            return 'success';
        } else if (lowerValue === 'pending' || lowerValue === 'processing') {
            return 'warning';
        } else if (lowerValue === 'rejected' || lowerValue === 'inactive' || lowerValue === 'exit') {
            return 'danger';
        }
        return 'secondary';
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.field !== columnKey) {
            return <FaSort className="ms-1 text-muted" size={12} />;
        }
        return sortConfig.direction === 'asc'
            ? <FaSortUp className="ms-1 text-primary" size={12} />
            : <FaSortDown className="ms-1 text-primary" size={12} />;
    };

    return (
        <>
            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                                <tr>
                                    {columns.map((column) => (
                                        <th
                                            key={column.key}
                                            onClick={() => column.sortable && onSort(column.key)}
                                            style={{
                                                cursor: column.sortable ? 'pointer' : 'default',
                                                padding: '16px',
                                                fontWeight: '600',
                                                fontSize: '14px',
                                                color: '#495057',
                                                borderBottom: '2px solid #dee2e6',
                                                userSelect: 'none'
                                            }}
                                        >
                                            {column.label}
                                            {column.sortable && getSortIcon(column.key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, index) => (
                                    <tr
                                        key={index}
                                        style={{
                                            transition: 'background-color 0.2s',
                                            backgroundColor: row.hasUpdates === 'Yes'
                                                ? '#fff8e1' // Light yellow for updated rows
                                                : (index % 2 === 0 ? '#ffffff' : '#f8f9fa')
                                        }}
                                    >
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                style={{
                                                    padding: '14px 16px',
                                                    fontSize: '13px',
                                                    color: '#212529'
                                                }}
                                            >
                                                {formatValue(row[column.key], column.format, row)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {pagination.totalPages > 1 && (
                <div className="mt-3">
                    <Pagination
                        currentPage={pagination.page}
                        totalPages={pagination.totalPages}
                        onPageChange={onPageChange}
                    />
                </div>
            )}
        </>
    );
};

export default ReportTable;
